/**
 * usePageContext — compute the Sancta "page awareness" payload sent with
 * every chat message so the LLM can answer "is this worth it?" without
 * the shopper having to describe what "this" is.
 *
 * Reads:
 *   - current URL path via useLocation
 *   - PDP product slug from `/shop/p/:slug` (regex)
 *   - AI Cart current external URL from local state (localStorage key
 *     `sb_ai_cart_focus_url`, set by AICart when a shopper opens a card)
 *   - live cart count from the marketplace-cart query cache
 */
import { useLocation } from "react-router-dom";
import { useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";

interface PageContext {
  path: string;
  route: string;
  surface: "shop" | "ai-cart" | "sancta" | "workspace" | "public" | "other";
  product_slug?: string;
  product_title?: string;
  product_score?: number;
  ai_cart_url?: string;
  ai_cart_marketplace?: string;
  cart_count?: number;
}

// Grab the current product slug when we're on /shop/p/:slug so Sancta can
// silently pull it into context without extra fetches.
function extractShopSlug(path: string): string | undefined {
  const m = path.match(/^\/shop\/p\/([^/?#]+)/);
  return m ? decodeURIComponent(m[1]) : undefined;
}

function classifySurface(path: string): PageContext["surface"] {
  if (path.startsWith("/shop")) return "shop";
  if (path.startsWith("/ai-cart")) return "ai-cart";
  if (path.startsWith("/sancta")) return "sancta";
  if (path.startsWith("/workspace") || path.startsWith("/admin")) return "workspace";
  if (path === "/" || path.startsWith("/login") || path.startsWith("/pricing")) return "public";
  return "other";
}

export function usePageContext(): PageContext {
  const loc = useLocation();
  const qc = useQueryClient();
  const path = loc.pathname;
  const slug = extractShopSlug(path);

  // Lazy fetch — only when we're on a PDP.
  const product = useQuery<any>({
    queryKey: ["shop-pdp-lite", slug],
    queryFn: async () => (await api.get(`/sancta/agent/product/${slug}`)).data,
    enabled: !!slug,
    staleTime: 30_000,
  });

  const ctx: PageContext = useMemo(() => {
    const base: PageContext = {
      path,
      route: path,
      surface: classifySurface(path),
    };
    if (slug) {
      base.product_slug = slug;
      if (product.data) {
        base.product_title = product.data.title;
        base.product_score = product.data.product_score;
      }
    }
    // AI Cart focused URL (set by AICart when the shopper opens a card).
    try {
      const focus = localStorage.getItem("sb_ai_cart_focus_url");
      if (focus) base.ai_cart_url = focus;
    } catch {}
    // Live cart count from the marketplace-cart cache (invalidated whenever
    // the shopper adds / removes items).
    try {
      const cart: any = qc.getQueryData(["marketplace-cart"]);
      const cnt = Array.isArray(cart?.items)
        ? cart.items.reduce((s: number, it: any) => s + (it.quantity || 1), 0)
        : undefined;
      if (typeof cnt === "number") base.cart_count = cnt;
    } catch {}
    return base;
  }, [path, slug, product.data, qc]);

  return ctx;
}
